/**
 * Which cars a bot drives, and with what seed.
 *
 * This is the whole of the mode -> roster decision, kept out of src/main.ts on
 * purpose: it is the part of "wire the bots in" that has interesting cases, and
 * it is the part a test can check without a WebGL context. main.ts's job is
 * reduced to calling `botRosterFor` once and putting the resulting inputs into
 * an array.
 *
 * ===========================================================================
 * THE RULE
 * ===========================================================================
 *
 * Every car is bot-driven EXCEPT the one the human is in, and — in plain
 * `doubles` — the human's team-mate, which that mode deliberately leaves idle.
 * That single exception is why this is a table-free function rather than a
 * `Record<GameMode, ...>`: the four modes differ in exactly two bits (is there a
 * human at all, does the human's team-mate drive) and spelling those out is
 * shorter and harder to get subtly wrong than four hand-written rosters.
 *
 *   duel              1v1   human + 1 opponent bot
 *   doubles           2v2   human + idle mate, 2 opponent bots
 *   doubles-bot-mate  2v2   human + 1 ally bot, 2 opponent bots
 *   bot-showcase      1v1   no human at all; both cars are bots
 *   freeplay          1     the human, alone; no second car exists
 *
 * `freeplay` is the one mode whose car count is not `teamSize * 2` — it is a
 * SOLO roster, and `carCount`/`teams` on the result are what say so. A caller
 * that sized the world from `teamSize` alone would park an unmanned opponent
 * on the halfway line, which is exactly the thing free play is the absence of.
 *
 * ===========================================================================
 * SEEDS
 * ===========================================================================
 *
 * A bot's generator is seeded from the WORLD's seed and its own car index, so a
 * match is reproducible from (world seed, human input log) alone — which is the
 * property test/bot/match.test.ts asserts over 2000 ticks. Deriving from the
 * car index rather than using it directly matters: `BotConfig.seed` defaults to
 * the car index, and car 0 and car 1 seeded 0 and 1 draw near-identical first
 * values out of mulberry32, so the two cars in a duel would start with
 * correlated aim errors.
 *
 * ===========================================================================
 * THE LEARNED TIER GETS HERE ONLY WITH A POLICY IN HAND
 * ===========================================================================
 *
 * `difficultyParams('learned')` throws, by design (see src/bot/difficulty.ts),
 * and `BotDirector` throws for a `learned` config that carries no `policy`.
 * Neither of those has been weakened. What changed is that a policy can now
 * EXIST: the player trains one, and `RosterOptions.learnedPolicy` is how the
 * caller says so.
 *
 * The gate is therefore the presence of that factory, not the tier's name. With
 * no factory the tier goes through `resolveDifficulty(tier, false)` exactly as
 * before and comes back scripted; with one, every bot config carries its own
 * `Policy` instance and the tier reaches `BotDirector` legally. This function is
 * still the last of the three independent refusals — the menu will not cycle
 * onto an unbacked `learned`, `sanitizeMatchSetup` will not store one, and this
 * will not build one.
 */

import type { BotConfig, Policy } from '../bot/bot';
import type { DifficultyTier } from '../bot/difficulty';
import {
  carCountForMode,
  isSpectator,
  playerCarIndex,
  resolveDifficulty,
  teamSizeForMode,
  type MatchSetup,
} from './settings';

export interface RosterOptions {
  /**
   * Builds the learned policy for one bot-driven car, or absent when nothing is
   * trained. Called ONCE PER CAR rather than shared: `Policy.reset` drops
   * per-episode memory, and two cars sharing one instance would reset each
   * other's.
   */
  learnedPolicy?: (carIndex: number) => Policy;
}

/** Odd 32-bit constant (2^32 / golden ratio); spreads adjacent car indices to
 *  unrelated corners of the generator's state space. */
const SEED_STRIDE = 0x9e3779b1;

/** The seed for the bot in car `carIndex` of a world seeded `worldSeed`. */
export const botSeed = (worldSeed: number, carIndex: number): number =>
  (Math.imul(carIndex + 1, SEED_STRIDE) + (worldSeed | 0)) >>> 0;

export interface BotRoster {
  /** Car the human drives, or null when the mode has no human in it. */
  humanCarIndex: number | null;
  /** Car the camera, HUD and boost meter follow. Always a real index — a
   *  spectator still has to be looking at something. */
  cameraCarIndex: number;
  /** Cars per team, so a caller can build the world without asking twice. */
  teamSize: number;
  /**
   * Cars on the pitch. `teamSize * 2` for every two-sided mode, and 1 for free
   * play — so a caller that blindly used `teamSize` would put a phantom
   * opponent on an empty pitch. See `carCountForMode`.
   */
  carCount: number;
  /** The team of each car, by index. What a caller hands `World` as its roster. */
  teams: (0 | 1)[];
  /** One entry per bot-driven car, ascending by car index. */
  configs: BotConfig[];
}

/**
 * The roster a setup implies. Total order over car indices matters: `World`
 * fills team 0 first, so cars `[0, teamSize)` are blue and the rest orange, and
 * `playerCarIndex` is defined against exactly that layout.
 */
export const botRosterFor = (
  setup: MatchSetup,
  worldSeed = 0,
  options: RosterOptions = {}
): BotRoster => {
  const teamSize = teamSizeForMode(setup.mode);
  const total = carCountForMode(setup.mode);
  const solo = total < teamSize * 2;

  // Clamped because `playerCarIndex` is computed from the mode's team size and
  // a caller could hand us a setup whose mode and team disagree.
  const cameraCarIndex = Math.min(Math.max(0, playerCarIndex(setup)), total - 1);
  const humanCarIndex = isSpectator(setup) ? null : cameraCarIndex;
  const learnedPolicy = options.learnedPolicy;
  const difficulty = resolveDifficulty(
    setup.difficulty,
    learnedPolicy !== undefined
  ) as DifficultyTier;

  // A solo roster is one car wearing the colour the player picked, so the
  // team-block arithmetic that maps index -> team does not apply to it.
  const teamOf = (index: number): 0 | 1 => (solo ? setup.team : index < teamSize ? 0 : 1);
  const humanTeam = teamOf(cameraCarIndex);
  /** `doubles` is "human plus an idle team-mate" — that IS the mode. */
  const idleTeammates = setup.mode === 'doubles' && humanCarIndex !== null;

  const configs: BotConfig[] = [];
  for (let i = 0; i < total; i++) {
    if (i === humanCarIndex) continue;
    if (idleTeammates && teamOf(i) === humanTeam) continue;
    const config: BotConfig = { carIndex: i, difficulty, seed: botSeed(worldSeed, i) };
    // Only when the tier survived resolution, which it does only if a factory
    // was supplied — so this can never emit a `learned` config with no policy.
    if (difficulty === 'learned' && learnedPolicy) config.policy = learnedPolicy(i);
    configs.push(config);
  }

  const teams = Array.from({ length: total }, (_, i) => teamOf(i));
  return { humanCarIndex, cameraCarIndex, teamSize, carCount: total, teams, configs };
};
